import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { RfButton } from '../../index'

import './Badge.scss'

const Badge = forwardRef(function Badge(props, ref) {
	const {
		className,
		component: Component = 'span',
		label,
		LabelProps,
		onClose,
		progress,
		size = 'medium',
		variant = 'default',
		...other
	} = props

	const handleClose = () => {
		typeof onClose === 'function' && onClose()
	}

	const closeButtonColor = {
		default: 'gray-1',
		pink: 'white',
		blue: 'white'
	}

	return (
		<Component
			ref={ref}
			className={clsx(
				'Vb-badge',
				onClose && 'Vb-badge--with-close-btn',
				size && `Vb-badge--${size}`,
				variant && `Vb-badge--${variant}`,
				className
			)}
			{...other}
		>
			{!!progress && (
				<span className="Vb-badge__progress">
					<span
						className="Vb-badge__progress-bar"
						role="progressbar"
						aria-valuemin="0"
						aria-valuemax="100"
						aria-valuenow={progress}
						style={{ width: `${progress}%` }}
					></span>
				</span>
			)}
			<span className="Vb-badge__label" {...LabelProps}>
				{label}
			</span>
			{onClose && (
				<RfButton
					icon="close"
					size="small"
					onClick={handleClose}
					className="Vb-badge__close-btn"
					IconProps={{ size: 0.5, color: closeButtonColor[variant] }}
				/>
			)}
		</Component>
	)
})

export default Badge

Badge.defaultProps = {
	component: 'span',
	size: 'medium',
	variant: 'default'
}

Badge.propTypes = {
	/**
	 * The component used for the root node.
	 * Either a string to use a HTML element or a component.
	 * @default 'span'
	 */
	component: PropTypes.elementType,

	/**
	 * The text to be used in an enclosing label element.
	 */
	label: PropTypes.node,

	/**
	 * Props applied to the `span` element that contains the label text.
	 */
	LabelProps: PropTypes.object,

	/**
	 * Callback fired when the close button is clicked.
	 * When the calback is passed, a small close button appears in the component.
	 *
	 * @param {object} event The event source of the callback.
	 */
	onClose: PropTypes.func,

	/**
	 * When a `number` is passed, a progress bar in the background appears. The number passed is the percent of the showed progress bar.
	 */
	progress: PropTypes.number,

	/**
	 * The size of the badge.
	 * @default 'medium'
	 */
	size: PropTypes.oneOf(['medium', 'small']),

	/**
	 * The variant of the badge.
	 * @default 'default'
	 */
	variant: PropTypes.oneOf(['default', 'pink', 'blue', 'energyLabel'])
}
